import { useState, useEffect, useMemo, useRef } from 'react';
import Services from '../services/Services';
import { Channel, EventListItem } from '../types';
import { registerAbortController, unregisterAbortController, isLoggingOut } from '../utils/requestCancellation';
import { useGeneral } from '../contexts';
import { getPublicToken } from '../utils/publicToken';

interface UseCommunityDetailsOptions {
  token: string | null;
  channelID: string | null;
  loadingKey?: string;
}

export const useCommunityDetails = (options: UseCommunityDetailsOptions) => {
  const { token, channelID, loadingKey } = options;
  const { setLoading, isLoading } = useGeneral();
  const [community, setCommunity] = useState<Channel | null>(null);
  const [events, setEvents] = useState<EventListItem[]>([]);
  const [localLoading, setLocalLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  // Use centralized loading if key is provided, otherwise use local state
  const loading = loadingKey ? isLoading(loadingKey) : localLoading;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    registerAbortController(abortController);

    const fetchCommunityDetails = async () => {
      if (!channelID || isLoggingOut()) {
        if (isMountedRef.current) {
          if (loadingKey) {
            setLoading(loadingKey, false);
          } else {
            setLocalLoading(false);
          }
        }
        return;
      }

      // Get token - use user token if available, otherwise get public token
      let effectiveToken = token;
      if (!effectiveToken) {
        effectiveToken = await getPublicToken();
      }

      if (!effectiveToken) {
        if (isMountedRef.current) {
          if (loadingKey) {
            setLoading(loadingKey, false);
          } else {
            setLocalLoading(false);
          }
          setError('Unable to fetch community details. Please try again later.');
        }
        return;
      }

      try {
        if (isMountedRef.current) {
          if (loadingKey) {
            setLoading(loadingKey, true);
          } else {
            setLocalLoading(true);
          }
          setError(null);
        }

        // Fetch channel details and events in parallel
        const [channelResponse, eventsResponse] = await Promise.all([
          Services.SocialHiveGetChannel(effectiveToken, channelID, abortController),
          Services.SocialHiveGetEvents(effectiveToken, channelID, abortController)
        ]);

        // Check if request was aborted or component unmounted
        if (abortController.signal.aborted || !isMountedRef.current) {
          return;
        }

        if (channelResponse.data.status && channelResponse.data.result) {
          setCommunity(channelResponse.data.result as Channel);
        } else {
          setError(channelResponse.data.message || 'Failed to fetch community');
        }

        if (eventsResponse.data.status && eventsResponse.data.result) {
          setEvents(eventsResponse.data.result);
        } else {
          // Events error is not critical
        }
      } catch (err: any) {
        // Ignore abort errors
        if (err.name === 'AbortError' || err.name === 'CanceledError' || err.message?.includes('Logout in progress')) {
          return;
        }

        if (isMountedRef.current) {
          setError(err.message || 'An error occurred');
        }
      } finally {
        if (isMountedRef.current && !abortController.signal.aborted) {
          if (loadingKey) {
            setLoading(loadingKey, false);
          } else {
            setLocalLoading(false);
          }
        } else if (loadingKey) {
          // Clear loading even if unmounted to prevent stuck state
          setLoading(loadingKey, false);
        }
      }
    };

    fetchCommunityDetails();

    return () => {
      abortController.abort();
      unregisterAbortController(abortController);
      // Clear loading state on cleanup to prevent stuck state
      if (loadingKey) {
        setLoading(loadingKey, false);
      }
    };
  }, [token, channelID, loadingKey, setLoading]);

  // Separate upcoming and past events
  const { upcomingEvents, pastEvents } = useMemo(() => {
    if (!events || events.length === 0) {
      return { upcomingEvents: [], pastEvents: [] };
    }

    const now = Date.now() / 1000; // Current time in seconds
    const upcoming: EventListItem[] = [];
    const past: EventListItem[] = [];

    events.forEach(event => {
      const eventEnd = event.eventEndDateTime;
      const eventStart = event.eventStartDateTime;
      // Event stays in upcoming until its end time passes (or start time if no end time)
      if ((eventEnd && eventEnd > now) || (!eventEnd && eventStart && eventStart > now)) {
        upcoming.push(event);
      } else {
        past.push(event);
      }
    });

    // Sort upcoming events by start date (ascending)
    upcoming.sort((a, b) => (a.eventStartDateTime || 0) - (b.eventStartDateTime || 0));
    // Sort past events by start date (descending)
    past.sort((a, b) => (b.eventStartDateTime || 0) - (a.eventStartDateTime || 0));

    return { upcomingEvents: upcoming, pastEvents: past };
  }, [events]);

  // Calculate statistics
  const participantCount = useMemo(() => {
    if (!community) return 0;
    // Try channelUserCount first (from API), then userCount, then user_count
    return Number(community.channelUserCount) || 
           Number(community.userCount) || 
           Number(community.user_count) || 
           Number(community.memberCount) || 
           0;
  }, [community]);

  const eventsCount = useMemo(() => {
    return events?.length || 0;
  }, [events]);

  return {
    community,
    events,
    upcomingEvents,
    pastEvents,
    participantCount,
    eventsCount,
    loading,
    error
  };
};

